import { Component, OnInit, inject } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'

import { DialogService } from '@hyperkit'

import { AuthService, LOGOUT_REASON } from '../../../auth/auth.service'
import { FrameDetectionService } from '../../../modules/shared/services/frame-detection.service'

import { ForgotPasswordDialogComponent } from './forgot-password-dialog/forgot-password-dialog.component'

@Component({
  standalone: false,
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)
  private authService = inject(AuthService)
  private dialogService = inject(DialogService)
  private frameDetectionService = inject(FrameDetectionService)

  public loginForm!: FormGroup
  public error = ''
  public signUpUrl = 'https://intel471.com/hunter-community-platform'

  private readonly logoutSources: { [source in LOGOUT_REASON]: string } = {
    [LOGOUT_REASON.EXPIRED]: 'Your session has expired, please login again.',
    [LOGOUT_REASON.REVOKED]: 'Your session has been revoked.',
  }

  constructor() {}

  public ngOnInit(): void {
    this.checkForLoginTokens()

    const source = this.activatedRoute.snapshot.queryParamMap.get('source')
    if (source) {
      this.error = this.logoutSources[source] || ''
    }

    this.loginForm = new FormGroup({
      username: new FormControl('', [Validators.required]),
      password: new FormControl(''),
    })
  }

  /**
   * Look for login tokens passed in query params, cache them, and redirect user as though they logged in.
   */
  private checkForLoginTokens(): void {
    const refreshToken =
      this.activatedRoute.snapshot.queryParamMap.get('refresh')
    const accessToken = this.activatedRoute.snapshot.queryParamMap.get('access')

    if (refreshToken && accessToken) {
      this.authService.setTokens({ accessToken, refreshToken })
      this.router.navigateByUrl(this.authService.getLoginRedirectPath(true))
    }
  }

  public onLogin(): void {
    const { username, password } = this.loginForm.value
    this.error = ''

    // Trim whitespace from password for users copying/pasting
    this.authService.login(username, password.trim()).subscribe({
      next: (response) => {
        if ('user' in response) {
          this.authService.setTokens(response.tokens)
          if (response.user.isFirstLogin) {
            this.router.navigateByUrl('new-password')
            return
          }
          this.router.navigateByUrl(this.authService.getLoginRedirectPath(true))
        } else if ('samlLoginUrl' in response) {
          window.location.href = response.samlLoginUrl
        } else if ('hasVerityAccess' in response) {
          // HUN-1337/HUN-1339: past the Verity cutoff, the backend gates the
          // login itself rather than issuing tokens, so there's no session
          // for AuthGuard to intercept — route the decision straight through.
          this.router.navigate(['verity-redirect'], {
            queryParams: { hasVerityAccess: response.hasVerityAccess },
          })
        } else {
          throw new Error('Unexpected error.')
        }
      },
      error: (error) => {
        this.error =
          error?.error?.message || 'Unable to login, please try again.'
      },
    })
  }

  public onForgotPassword(): void {
    this.dialogService.open(ForgotPasswordDialogComponent, {
      panelClass: 'hyperkit-component', // You can customize this per dialog if needed
    })
  }
}
